/**
 * add-member-helpers.ts — Hàm thuần cho hộp thoại "Thêm nhân viên con người":
 * kiểm email + gợi ý gõ nhầm nhà cung cấp, sinh mật khẩu mạnh, dựng danh sách vai trò, soạn tin gửi nhân viên.
 * Server kiểm lại định dạng + DNS tên miền (`ecdp_backend_source/src/modules/settings/member-invite.ts`).
 */
import type { RoleSummary } from '@/hooks/use-settings'
import type { Role } from '@/types/api'
import { platformLabelOf } from '@/lib/platform-labels'
import { isValidVnPhone } from '@/pages/auth/auth-helpers'
import { i18n } from '@/lib/i18n'

// ── Email ───────────────────────────────────────────────────────────

export const EMAIL_MAX = 254
const LOCAL_RE = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*$/
const LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/
const TLD_RE = /^(?:[a-z]{2,63}|xn--[a-z0-9-]{1,59})$/

export function normalizeEmail(raw: string): string {
  return raw.trim().toLowerCase()
}

/** Lỗi định dạng (null = hợp lệ). Nhận email đã chuẩn hoá. Cùng luật với backend. */
export function emailFormatError(email: string): string | null {
  if (!email) return i18n.t('team:addMemberHelpers.email.required')
  if (email.length > EMAIL_MAX) return i18n.t('team:addMemberHelpers.email.tooLong', { max: EMAIL_MAX })
  const at = email.lastIndexOf('@')
  if (at <= 0 || email.indexOf('@') !== at) return i18n.t('team:addMemberHelpers.email.atInvalid')
  const local = email.slice(0, at)
  const domain = email.slice(at + 1)
  if (local.length > 64 || !LOCAL_RE.test(local)) return i18n.t('team:addMemberHelpers.email.localInvalid')
  const labels = domain.split('.')
  if (domain.length > 253 || labels.length < 2 || !labels.every((l) => LABEL_RE.test(l))) {
    return i18n.t('team:addMemberHelpers.email.domainInvalid')
  }
  if (!TLD_RE.test(labels[labels.length - 1])) return i18n.t('team:addMemberHelpers.email.tldInvalid')
  return null
}

/** Nhà cung cấp phổ biến — gõ gần giống (≤ 2 ký tự) thì gợi ý sửa. */
export const COMMON_EMAIL_DOMAINS = [
  'gmail.com',
  'yahoo.com',
  'outlook.com',
  'hotmail.com',
  'icloud.com',
  'live.com',
  'ymail.com',
] as const

/** Tên miền thật, gần giống nhà cung cấp phổ biến — không gợi ý sửa. */
const KNOWN_REAL_DOMAINS = new Set([
  ...COMMON_EMAIL_DOMAINS,
  'mail.com', 'email.com', 'gmx.com', 'aol.com', 'me.com', 'mac.com', 'msn.com', 'zoho.com',
  'yahoo.com.vn', 'yahoo.co.uk', 'yahoo.co.jp', 'hotmail.co.uk', 'outlook.com.vn', 'googlemail.com',
  'proton.me', 'protonmail.com', 'yandex.com', 'fpt.com.vn', 'fpt.vn', 'vnpt.vn', 'viettel.com.vn',
])

/** Đuôi hay gõ nhầm (áp cho mọi tên miền). */
const TLD_TYPOS: Record<string, string> = {
  con: 'com', cmo: 'com', ocm: 'com', vom: 'com', xom: 'com', comm: 'com', cpm: 'com', coom: 'com', cim: 'com',
  nv: 'vn', vnn: 'vn',
}

/** Khoảng cách chỉnh sửa (Damerau–Levenshtein, bản "optimal string alignment"). */
export function editDistance(a: string, b: string): number {
  const d: number[][] = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)])
  for (let j = 1; j <= b.length; j++) d[0][j] = j
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + cost)
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) {
        d[i][j] = Math.min(d[i][j], d[i - 2][j - 2] + 1)
      }
    }
  }
  return d[a.length][b.length]
}

/**
 * Gợi ý sửa email gõ nhầm tên miền: "abc@fmail.com" → "abc@gmail.com".
 * Trả null nếu không nghi gõ nhầm. Chỉ xét email đúng dạng có một ký tự @.
 */
export function suggestEmail(rawEmail: string): string | null {
  const email = normalizeEmail(rawEmail)
  const at = email.lastIndexOf('@')
  if (at <= 0 || email.indexOf('@') !== at) return null
  const local = email.slice(0, at)
  const domain = email.slice(at + 1)
  if (!domain || KNOWN_REAL_DOMAINS.has(domain)) return null

  let best: { d: string; dist: number } | null = null
  if (domain.length >= 4) {
    for (const d of COMMON_EMAIL_DOMAINS) {
      const dist = editDistance(domain, d)
      if (dist <= 2 && (!best || dist < best.dist)) best = { d, dist }
    }
  }
  if (best) return `${local}@${best.d}`

  const dot = domain.lastIndexOf('.')
  if (dot > 0) {
    const fixed = TLD_TYPOS[domain.slice(dot + 1)]
    if (fixed) return `${local}@${domain.slice(0, dot + 1)}${fixed}`
  }
  return null
}

// ── Mật khẩu ────────────────────────────────────────────────────────

// Bỏ ký tự dễ nhầm: 0 O o, 1 l I i, và các ký hiệu khó đọc / khó gõ (| ` ' " \ , . ;)
export const PW_LOWER = 'abcdefghjkmnpqrstuvwxyz'
export const PW_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ'
export const PW_DIGITS = '23456789'
export const PW_SYMBOLS = '!@#$%&*?-_=+'
export const PW_ALPHABET = PW_LOWER + PW_UPPER + PW_DIGITS + PW_SYMBOLS

/** Số ngẫu nhiên an toàn trong [0, max) — lấy mẫu loại bỏ để không lệch phân bố. */
function secureIndex(max: number, rand: (n: number) => Uint32Array): number {
  const limit = Math.floor(0x1_0000_0000 / max) * max
  for (;;) {
    const v = rand(1)[0]
    if (v < limit) return v % max
  }
}

const cryptoRand = (n: number) => crypto.getRandomValues(new Uint32Array(n))

/** Mật khẩu mạnh (mặc định 14 ký tự, tối thiểu 12): đủ chữ thường, chữ hoa, số, ký hiệu; không ký tự dễ nhầm. */
export function generateStrongPassword(length = 14, rand: (n: number) => Uint32Array = cryptoRand): string {
  const len = Math.max(12, Math.min(64, Math.floor(length)))
  const pick = (set: string) => set[secureIndex(set.length, rand)]
  const chars = [pick(PW_LOWER), pick(PW_UPPER), pick(PW_DIGITS), pick(PW_SYMBOLS)]
  while (chars.length < len) chars.push(pick(PW_ALPHABET))
  // Xáo Fisher–Yates để 4 ký tự bắt buộc không luôn nằm đầu.
  for (let i = chars.length - 1; i > 0; i--) {
    const j = secureIndex(i + 1, rand)
    ;[chars[i], chars[j]] = [chars[j], chars[i]]
  }
  return chars.join('')
}

// ── Vai trò ─────────────────────────────────────────────────────────

export interface RoleOption {
  /** id vai trò (RBAC) — hoặc `sys:<role>` khi không đọc được danh sách vai trò (dùng API cũ `role`). */
  value: string
  label: string
  description: string | null
  systemKey: string | null
  isSystem: boolean
}

export const ROLE_FALLBACK_LABELS: Record<Exclude<Role, 'owner'>, string> = {
  get admin() { return i18n.t('common:roles.admin') },
  get manager() { return i18n.t('common:roles.manager') },
  get member() { return i18n.t('common:roles.member') },
}

const SYSTEM_ORDER: Record<string, number> = { admin: 0, manager: 1, member: 2 }

/** Chỉ chủ sở hữu / quản trị viên được gán "Quản trị viên" (khớp chốt ở backend). */
export function canAssignAdmin(myRole: Role | undefined | null): boolean {
  return myRole === 'owner' || myRole === 'admin'
}

/**
 * Danh sách vai trò chọn được khi thêm nhân viên: bỏ Chủ sở hữu, bỏ Quản trị viên nếu người đang thao tác
 * không phải owner/admin. Vai trò hệ thống trước (Quản trị viên → Quản lý → Nhân viên), rồi vai trò tự tạo A→Z.
 * `roles` null/rỗng (không có quyền xem vai trò, hoặc lỗi) → danh sách cố định theo vai trò hệ thống.
 */
export function buildRoleOptions(roles: RoleSummary[] | null | undefined, myRole: Role | undefined | null): RoleOption[] {
  const allowAdmin = canAssignAdmin(myRole)
  if (!roles || roles.length === 0) {
    return (['admin', 'manager', 'member'] as const)
      .filter((k) => allowAdmin || k !== 'admin')
      .map((k) => ({ value: `sys:${k}`, label: ROLE_FALLBACK_LABELS[k], description: null, systemKey: k, isSystem: true }))
  }
  return roles
    .filter((r) => r.systemKey !== 'owner' && (allowAdmin || r.systemKey !== 'admin'))
    .map((r) => ({
      value: r.id,
      label: r.name,
      description: r.description?.trim() || null,
      systemKey: r.systemKey,
      isSystem: r.isSystem || !!r.systemKey,
    }))
    .sort((a, b) => {
      const oa = a.systemKey ? SYSTEM_ORDER[a.systemKey] ?? 5 : 10
      const ob = b.systemKey ? SYSTEM_ORDER[b.systemKey] ?? 5 : 10
      return oa - ob || a.label.localeCompare(b.label, 'vi')
    })
}

/** Mặc định chọn "Nhân viên" (systemKey member); không có thì mục đầu tiên. */
export function defaultRoleValue(options: RoleOption[]): string {
  return (options.find((o) => o.systemKey === 'member') ?? options[0])?.value ?? 'sys:member'
}

/**
 * Vai trò đang gán của một nhân viên, dưới dạng giá trị của ô chọn (hộp "Sửa nhân viên"). Ưu tiên vai trò động
 * (`roleId` — thứ quyết định quyền thật), rồi vai trò hệ thống cùng `role`, cuối cùng `sys:<role>`.
 * Vai trò hiện tại không nằm trong danh sách được gán (vd người sửa là Quản lý, người bị sửa là Quản trị viên)
 * → thêm một mục chỉ để hiển thị, để ô chọn không bị trống.
 */
export function memberRoleSelection(
  member: { role: Role; roleId?: string | null; roleName?: string | null },
  options: RoleOption[],
): { value: string; options: RoleOption[] } {
  const byId = member.roleId ? options.find((o) => o.value === member.roleId) : undefined
  const bySystem = options.find((o) => o.systemKey === member.role && (o.value.startsWith('sys:') || !member.roleId))
  const found = byId ?? bySystem
  if (found) return { value: found.value, options }
  const value = member.roleId ?? `sys:${member.role}`
  const label = member.roleName || (member.role === 'owner' ? i18n.t('common:roles.owner') : ROLE_FALLBACK_LABELS[member.role])
  return { value, options: [...options, { value, label, description: null, systemKey: member.role, isSystem: true }] }
}

/** Giá trị chọn → body API: `roleId` (RBAC) hoặc `role` (dự phòng). */
export function rolePayload(value: string): { roleId: string } | { role: Exclude<Role, 'owner'> } {
  if (value.startsWith('sys:')) return { role: value.slice(4) as Exclude<Role, 'owner'> }
  return { roleId: value }
}

// ── Tin nhắn gửi nhân viên mới ──────────────────────────────────────

export interface NewMemberInfo {
  fullName: string
  email: string
  roleName: string
  password: string
  loginUrl: string
  orgName?: string | null
  /** SĐT hiển thị (dạng 0xxxxxxxxx). */
  phone?: string | null
  teamName?: string | null
  /** Kênh phụ trách đã giao. */
  channels?: ChannelBrief[]
}

// ── Số điện thoại ───────────────────────────────────────────────────

/** Lỗi SĐT (null = hợp lệ hoặc bỏ trống). Cùng luật với đăng ký + backend `normalizeVnPhone`. */
export function memberPhoneError(raw: string): string | null {
  if (!raw.trim()) return null
  return isValidVnPhone(raw) ? null : i18n.t('team:addMemberHelpers.phoneInvalid')
}

/** 84xxxxxxxxx (dạng lưu ở server) → 0xxxxxxxxx để hiển thị / gửi nhân viên. */
export function displayVnPhone(phone: string | null | undefined): string {
  if (!phone) return ''
  return phone.startsWith('84') && phone.length === 11 ? `0${phone.slice(2)}` : phone
}

// ── Kênh phụ trách ──────────────────────────────────────────────────

export interface ChannelBrief {
  name: string
  platformLabel: string
}

/** Một dòng trong danh sách chọn "Kênh phụ trách". */
export interface ChannelOption {
  id: string
  name: string
  platform: number
  platformLabel: string
  /** Đang kết nối (theo liveStatus). */
  online: boolean
  /** Bị tắt (tổ chức tắt hoặc quản trị hệ thống khoá) — hiện mờ, không nằm trong "Chọn tất cả". */
  disabled: boolean
}

type AccountLike = {
  id: string
  platform: number
  displayName: string | null
  isDisabled?: boolean
  lockedByPlatform?: boolean
  status?: string | null
  liveStatus?: string | null
}

export function toChannelOptions(accounts: AccountLike[] | null | undefined): ChannelOption[] {
  return (accounts ?? []).map((a) => ({
    id: a.id,
    name: a.displayName?.trim() || i18n.t('team:addMemberHelpers.unnamed'),
    platform: a.platform,
    platformLabel: platformLabelOf(a.platform),
    online: (a.liveStatus ?? a.status) === 'connected',
    disabled: !!a.isDisabled || !!a.lockedByPlatform,
  }))
}

const fold = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase()

/** Lọc theo tên hoặc nhãn nền tảng — không phân biệt hoa/thường, dấu tiếng Việt. */
export function filterChannelOptions(options: ChannelOption[], query: string): ChannelOption[] {
  const q = fold(query.trim())
  if (!q) return options
  return options.filter((o) => fold(o.name).includes(q) || fold(o.platformLabel).includes(q))
}

export function toggleChannel(selected: string[], id: string): string[] {
  return selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]
}

/** "Chọn tất cả" — thêm mọi kênh đang HIỂN THỊ và không bị tắt; giữ các kênh đã chọn khác. */
export function selectAllChannels(selected: string[], visible: ChannelOption[]): string[] {
  const out = [...selected]
  for (const o of visible) if (!o.disabled && !out.includes(o.id)) out.push(o.id)
  return out
}

/** "Bỏ chọn" — gỡ mọi kênh đang hiển thị (kể cả kênh tắt đã chọn trước đó). */
export function clearChannels(selected: string[], visible: ChannelOption[]): string[] {
  const hide = new Set(visible.map((o) => o.id))
  return selected.filter((id) => !hide.has(id))
}

/** Mọi kênh hiển thị (không tắt) đều đã chọn → nút đổi thành "Bỏ chọn". */
export function allVisibleSelected(selected: string[], visible: ChannelOption[]): boolean {
  const pickable = visible.filter((o) => !o.disabled)
  return pickable.length > 0 && pickable.every((o) => selected.includes(o.id))
}

/** Kênh đã chọn theo thứ tự chọn (bỏ id không còn trong danh sách). */
export function selectedChannelBriefs(selected: string[], options: ChannelOption[]): ChannelBrief[] {
  const byId = new Map(options.map((o) => [o.id, o]))
  return selected.flatMap((id) => {
    const o = byId.get(id)
    return o ? [{ name: o.name, platformLabel: o.platformLabel }] : []
  })
}

/** Kết quả API → tóm tắt kênh. */
export function channelBriefsFromApi(channels: { displayName: string | null; platform: number }[] | null | undefined): ChannelBrief[] {
  return (channels ?? []).map((c) => ({ name: c.displayName?.trim() || i18n.t('team:addMemberHelpers.unnamed'), platformLabel: platformLabelOf(c.platform) }))
}

/**
 * "2 kênh — Zalo Shop (Zalo cá nhân), Fanpage (Facebook Messenger)".
 * `max` giới hạn số tên liệt kê (phần còn lại "và N kênh khác"); bỏ trống → liệt kê hết.
 */
export function channelSummary(channels: ChannelBrief[] | null | undefined, max?: number): string {
  const list = channels ?? []
  if (list.length === 0) return i18n.t('team:addMemberHelpers.noChannelsAssigned')
  const shown = max && max > 0 ? list.slice(0, max) : list
  const names = shown.map((c) => `${c.name} (${c.platformLabel})`).join(', ')
  const rest = list.length - shown.length
  return i18n.t('team:addMemberHelpers.channelSummary', { count: list.length, names }) + (rest > 0 ? i18n.t('team:addMemberHelpers.andMoreChannels', { count: rest }) : '')
}

export function loginUrlFor(origin: string): string {
  return `${origin.replace(/\/+$/, '')}/login`
}

/** Tin nhắn sẵn để gửi cho nhân viên (nút "Sao chép tất cả"). */
export function buildInviteMessage(info: NewMemberInfo): string {
  const name = info.fullName.trim() || info.email
  const org = info.orgName?.trim() || i18n.t('team:addMemberHelpers.defaultOrgName')
  return [
    i18n.t('team:addMemberHelpers.invite.greeting', { name, org }),
    i18n.t('team:addMemberHelpers.invite.loginUrl', { value: info.loginUrl }),
    i18n.t('team:addMemberHelpers.invite.email', { value: info.email }),
    i18n.t('team:addMemberHelpers.invite.password', { value: info.password }),
    i18n.t('team:addMemberHelpers.invite.role', { value: info.roleName }),
    ...(info.teamName ? [i18n.t('team:addMemberHelpers.invite.team', { value: info.teamName })] : []),
    ...(info.phone ? [i18n.t('team:addMemberHelpers.invite.phone', { value: info.phone })] : []),
    ...(info.channels && info.channels.length > 0 ? [i18n.t('team:addMemberHelpers.invite.channels', { value: channelSummary(info.channels) })] : []),
    i18n.t('team:addMemberHelpers.invite.changePasswordHint'),
  ].join('\n')
}
